import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 数学公式动画 - 二次方程
 * 
 * 核心效果：
 * 1. 公式逐字符出现
 * 2. 变量高亮动画
 * 3. 求解步骤展开
 * 4. 图形辅助说明
 */

export const MathFormulaAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleY = interpolate(frame, [0, 30], [-50, 0], { extrapolateRight: 'clamp' });

  // 公式动画（逐字符）
  const formula = 'ax² + bx + c = 0';
  const charsVisible = Math.floor(interpolate(frame, [30, 90], [0, formula.length], { extrapolateRight: 'clamp' }));
  const visibleFormula = formula.substring(0, charsVisible);

  // 变量高亮
  const highlightA = interpolate(frame, [100, 120, 140, 160], [0, 1, 1, 0], { extrapolateRight: 'clamp' });
  const highlightB = interpolate(frame, [170, 190, 210, 230], [0, 1, 1, 0], { extrapolateRight: 'clamp' });
  const highlightC = interpolate(frame, [240, 260, 280, 300], [0, 1, 1, 0], { extrapolateRight: 'clamp' });

  // 求解公式动画
  const solutionOpacity = interpolate(frame, [320, 360], [0, 1], { extrapolateRight: 'clamp' });
  const solutionScale = spring({
    frame: frame - 320,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  // 步骤动画
  const steps = [
    { label: '判别式 Δ = b² - 4ac', delay: 380 },
    { label: 'Δ > 0 → 两个实根', delay: 420 },
    { label: 'Δ = 0 → 一个实根', delay: 460 },
    { label: 'Δ < 0 → 无实根', delay: 500 },
  ];

  return (
    <AbsoluteFill style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      fontFamily: 'Georgia, serif',
      color: 'white',
      padding: 80,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 80,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `translateY(${titleY}px)`,
      }}>
        <h1 style={{
          fontSize: 80,
          fontWeight: 700,
          margin: 0,
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          二次方程
        </h1>
        <p style={{
          fontSize: 40,
          marginTop: 20,
          opacity: 0.8,
        }}>
          Quadratic Equation
        </p>
      </div>

      {/* 公式展示 */}
      <div style={{
        position: 'absolute',
        top: '35%',
        left: 0,
        right: 0,
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: 120,
          fontFamily: 'Times New Roman, serif',
          fontStyle: 'italic',
          display: 'inline-block',
          padding: '40px 80px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 30,
          border: '4px solid rgba(255, 255, 255, 0.3)',
        }}>
          {visibleFormula}
          <span style={{ opacity: charsVisible < formula.length ? 0.3 : 0 }}>|</span>
        </div>
      </div>

      {/* 变量说明 */}
      <div style={{
        position: 'absolute',
        top: '60%',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 100,
      }}>
        {[
          { symbol: 'a', desc: '二次项系数', highlight: highlightA },
          { symbol: 'b', desc: '一次项系数', highlight: highlightB },
          { symbol: 'c', desc: '常数项', highlight: highlightC },
        ].map((item, i) => (
          <div key={i} style={{
            textAlign: 'center',
            padding: '30px 40px',
            background: `rgba(102, 126, 234, ${item.highlight * 0.3})`,
            borderRadius: 20,
            border: `3px solid rgba(102, 126, 234, ${item.highlight})`,
            transition: 'all 0.3s ease',
          }}>
            <div style={{
              fontSize: 60,
              fontFamily: 'Times New Roman, serif',
              fontStyle: 'italic',
            }}>
              {item.symbol}
            </div>
            <div style={{
              fontSize: 24,
              marginTop: 10,
              opacity: 0.8,
            }}>
              {item.desc}
            </div>
          </div>
        ))}
      </div>

      {/* 求解公式 */}
      <div style={{
        position: 'absolute',
        top: '79%',
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: solutionOpacity,
        transform: `scale(${solutionScale})`,
      }}>
        <div style={{
          fontSize: 70,
          fontFamily: 'Times New Roman, serif',
          fontStyle: 'italic',
          padding: '30px 60px',
          background: 'rgba(118, 75, 162, 0.3)',
          borderRadius: 20,
          display: 'inline-block',
        }}>
          x = (-b ± √Δ) / 2a
        </div>
      </div>

      {/* 求解步骤 */}
      <div style={{
        position: 'absolute',
        bottom: 60,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 40,
      }}>
        {steps.map((step, i) => {
          const stepOpacity = interpolate(
            frame,
            [step.delay, step.delay + 20],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );

          return (
            <div key={i} style={{
              fontSize: 28,
              padding: '15px 30px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 15,
              opacity: stepOpacity,
            }}>
              {step.label}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
